import React, { useMemo, useState } from 'react';
import { FEATURES } from '../config/features';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { TaskStatusLabels, TaskStatusColors, TaskStatus, User } from '../types';
import { getAllSubElements, calculateActualHours } from '../data/mockData';
import { formatHours } from '../utils/format';
import { Avatar } from '../components/common/Avatar';
import { UsersIcon } from '../components/icons/Icons';
import { AppSectionHero, AppStatCard, AppToolbar } from '../components/common/AppSurface';
import './TeamPage.css';

interface MemberStats {
  user: User;
  id: string;
  name: string;
  email: string;
  role: string;
  totalTasks: number;
  tasksByStatus: Record<TaskStatus, number>;
  estimatedHours: number;
  actualHours: number;
  completionRate: number;
}

const formatMemberName = (name: string): string => {
  const parts = name.split(' ').filter(Boolean);
  const capitalized = parts.map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase());
  if (capitalized.length <= 3) return capitalized.join(' ');
  return `${capitalized[0]} ${capitalized[parts.length > 2 ? 2 : 1]}`;
};

export const TeamPage: React.FC = () => {
  const { isProjectManager } = useAuth();
  const { activeProject, activeProjectMembers } = useProjects();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'ALL' | 'PROJECT_MANAGER' | 'TECHNICIAN'>('ALL');

  const teamStats: MemberStats[] = useMemo(() => {
    if (!activeProject) return [];
    const allSubs = getAllSubElements(activeProject);
    const techUsers = activeProjectMembers;

    return techUsers.map(user => {
      const tasks = allSubs.filter(s => s.assignedTo.includes(user.id));
      const byStatus: Record<TaskStatus, number> = {
        ASSIGNED: 0,
        STARTED: 0,
        IN_PROGRESS: 0,
        IN_REVIEW: 0,
        PAUSED: 0,
        FINISHED: 0,
        ISSUES: 0,
      };
      tasks.forEach(t => { byStatus[t.status] = (byStatus[t.status] || 0) + 1; });
      const estHours = tasks.reduce((sum, t) => sum + t.estimatedHours, 0);
      const actHours = tasks.reduce((sum, t) => sum + calculateActualHours(t.timeEntries), 0);
      const completionRate = tasks.length > 0 ? (byStatus.FINISHED / tasks.length) * 100 : 0;

      return {
        user,
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        totalTasks: tasks.length,
        tasksByStatus: byStatus,
        estimatedHours: estHours,
        actualHours: actHours,
        completionRate,
      };
    });
  }, [activeProject, activeProjectMembers]);

  const filteredStats = useMemo(() => {
    return teamStats.filter(m => {
      const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === 'ALL' || m.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [teamStats, searchTerm, roleFilter]);

  if (!isProjectManager) {
    return (
      <div className="team-container">
        <AppSectionHero
          icon={<UsersIcon size={28} />}
          title="Team"
          subtitle="Only Project Managers can access this page."
        />
        <p style={{ textAlign: 'center', color: 'var(--text-3)' }}>Only Project Managers can access this page.</p>
      </div>
    );
  }

  return (
    <div className="team-container">
      <AppSectionHero
        icon={<UsersIcon size={28} />}
        title="Team Overview"
        subtitle={activeProject ? <>{activeProject.name} {'\u2014'} <strong>{activeProject.code}</strong></> : 'No active project'}
        aside={
          <>
            <AppStatCard icon={<UsersIcon size={20} />} label="Members" value={teamStats.length} caption="Visible team members" tone="blue" />
            <AppStatCard icon={<UsersIcon size={20} />} label="Assignments" value={teamStats.reduce((s, m) => s + m.totalTasks, 0)} caption="Tasks across the team" tone="mint" />
          </>
        }
      />

      {/* Filters */}
      <AppToolbar
        className="team-filters"
        left={
          <input
            className="app-surface-input team-search-input"
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        }
        right={
          <>
            <select
              className="app-surface-select team-role-filter"
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value as typeof roleFilter)}
            >
              <option value="ALL">All Roles</option>
              <option value="PROJECT_MANAGER">Project Manager</option>
              <option value="TECHNICIAN">Technician</option>
            </select>
            {(searchTerm || roleFilter !== 'ALL') && (
              <button
                className="app-surface-btn team-filter-clear"
                onClick={() => { setSearchTerm(''); setRoleFilter('ALL'); }}
              >
                Clear
              </button>
            )}
          </>
        }
      />

      {/* Summary Cards */}
      <div className="team-summary">
        <AppStatCard icon={<UsersIcon size={20} />} label="Technicians" value={teamStats.length} caption="Available in this project" tone="blue" />
        <AppStatCard icon={<UsersIcon size={20} />} label="Assignments" value={teamStats.reduce((s, m) => s + m.totalTasks, 0)} caption="Total workload" tone="mint" />
        {FEATURES.TIME_TRACKING && (
          <AppStatCard
            icon={<UsersIcon size={20} />}
            label="Est. Hours"
            value={formatHours(teamStats.reduce((s, m) => s + m.estimatedHours, 0))}
            caption="Planned team effort"
          />
        )}
      </div>

      {/* Member Cards */}
      <div className="team-grid">
        {filteredStats.map(member => (
          <div key={member.id} className="team-member-card">
            <div className="team-member-header">
              <Avatar user={member.user} size={42} className="team-member-avatar" />
              <div className="team-member-info">
                <span className="team-member-name">{formatMemberName(member.name)}</span>
                <span className="team-member-email">{member.email}</span>
              </div>
            </div>

            {/* Workload Bar */}
            <div className="team-workload-bar">
              {(Object.keys(member.tasksByStatus) as TaskStatus[]).map(status => {
                const count = member.tasksByStatus[status];
                if (count === 0) return null;
                const pct = member.totalTasks > 0 ? (count / member.totalTasks) * 100 : 0;
                return (
                  <div
                    key={status}
                    className="team-workload-segment"
                    style={{ width: `${pct}%`, backgroundColor: TaskStatusColors[status] }}
                    title={`${TaskStatusLabels[status]}: ${count}`}
                  />
                );
              })}
            </div>

            {/* Stats */}
            <div className="team-member-stats">
              <div className="team-member-stat-row">
                <span className="team-stat-label">Tasks</span>
                <span className="team-stat-value">{member.totalTasks}</span>
              </div>
              <div className="team-member-stat-row">
                <span className="team-stat-label">Completed</span>
                <span className="team-stat-value" style={{ color: 'var(--accent-green)' }}>
                  {member.tasksByStatus.FINISHED}
                </span>
              </div>
              <div className="team-member-stat-row">
                <span className="team-stat-label">In Progress</span>
                <span className="team-stat-value" style={{ color: 'var(--accent-amber)' }}>
                  {member.tasksByStatus.IN_PROGRESS + member.tasksByStatus.STARTED}
                </span>
              </div>
              <div className="team-member-stat-row">
                <span className="team-stat-label">Issues</span>
                <span className="team-stat-value" style={{ color: 'var(--accent-red)' }}>
                  {member.tasksByStatus.ISSUES}
                </span>
              </div>
              {FEATURES.TIME_TRACKING && <>
                <div className="team-member-stat-row">
                  <span className="team-stat-label">Est. Hours</span>
                  <span className="team-stat-value">{formatHours(member.estimatedHours)}</span>
                </div>
                <div className="team-member-stat-row">
                  <span className="team-stat-label">Actual Hours</span>
                  <span
                    className="team-stat-value"
                    style={{
                      color: member.actualHours > member.estimatedHours
                        ? 'var(--accent-red)'
                        : 'var(--accent-green)',
                    }}
                  >
                    {formatHours(member.actualHours)}
                  </span>
                </div>
              </>}
            </div>

            {/* Completion */}
            <div className="team-completion">
              <div className="team-completion-bar-wrap">
                <div
                  className="team-completion-bar-fill"
                  style={{ width: `${member.completionRate}%` }}
                />
              </div>
              <span className="team-completion-pct">{member.completionRate.toFixed(0)}%</span>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};
